import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api, ENDPOINTS } from '../services';
import { toast } from 'react-toastify';
import { TOAST_MESSAGES } from '../shared/appConstants';

const initialState = {
  dashboard: null,
  dashboardLoading: false,
  myTickets: null,
  myTicketsLoading: false,
  sprintsTickets: [],
  sprintsTicketsLoading: false,
  sprintTimeline: null,
  sprintTimelineLoading: false,
  sprintSummary: null,
  sprintSummaryLoading: false,
  sprintDetails: null,
  sprintDetailsLoading: false,
  sprintVersions: [],
  sprintVersionsLoading: false,
  loading: false,
  error: null,
  scrumRole: null,
  scrumRoleLoading: false,
  sprintSettings: null,
  sprintSettingsLoading: false,
  sprintManagement: null,
  sprintManagementLoading: false,
  sprintManagementError: null,
  sprintManagementRequestId: null,
};

export const fetchSprintManagement = createAsyncThunk(
  'scrum/fetchSprintManagement',
  async ({ projectId, status }, { rejectWithValue }) => {
    try {
      const healthResponse = await api.get(
        ENDPOINTS.SCRUM_SPRINT_MANAGEMENT(status, projectId, 'sp_health')
      );
      const healthPayload = healthResponse.data?.data || healthResponse.data;
      const { details: healthDetails, ...snapshotData } = healthPayload || {};
      const statusKey = status ? status.toLowerCase() : '';
      const selectedHealth = healthDetails?.health;

      const snapshot = {
        ...snapshotData,
        [statusKey]: snapshotData?.[statusKey]?.id ? {
          ...snapshotData[statusKey],
          ...(selectedHealth ? {
            complete_story_points: selectedHealth.story_points?.completed,
            incomplete_story_points: selectedHealth.story_points?.incomplete,
            total_story_points: selectedHealth.story_points?.committed,
            velocity: selectedHealth.velocity,
            progress: selectedHealth.progress
          } : {})
        } : snapshotData?.[statusKey],
      };

      if (!snapshotData?.[statusKey]?.id) {
        return {
          status,
          snapshot,
          health: healthDetails || null,
          charts: null,
          summary: null,
        };
      }

      let chartsPayload = null;
      let summaryPayload = null;

      await Promise.all([
        api.get(ENDPOINTS.SCRUM_SPRINT_MANAGEMENT(status, projectId, 'charts'))
          .then(res => { chartsPayload = res.data?.data || res.data; })
          .catch(err => console.error('Failed to fetch sprint charts', err)),
        api.get(ENDPOINTS.SCRUM_SPRINT_MANAGEMENT(status, projectId, 'summary'))
          .then(res => { summaryPayload = res.data?.data || res.data; })
          .catch(err => console.error('Failed to fetch sprint summary', err))
      ]);


      return {
        status,
        snapshot,
        health: healthDetails || null,
        charts: chartsPayload?.details || null,
        summary: summaryPayload?.details || null,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch sprint management data'
      );
    }
  }
);

export const fetchSprintSettings = createAsyncThunk('scrum/fetchSprintSettings', async (_, { rejectWithValue }) => {
  try {
    const response = await api.get(ENDPOINTS.SPRINT_SETTINGS);
    if (response.status !== 200) {
      throw new Error(`HTTP ${response.status}`);
    }
    return response.data;
  } catch (error) {
    const errorMsg = error.response?.status === 404 
      ? 'Sprint settings not found' 
      : error.message;
    return rejectWithValue(errorMsg);
  }
});

export const updateSprintSettings = createAsyncThunk('scrum/updateSprintSettings', async ({ storyPoints, sprintDuration }, { rejectWithValue }) => {
  try {
    const payload = {
      json_value: {
        duration: sprintDuration?.toString().replace(/[^0-9]/g, ''),
        max_story_points: storyPoints?.toString()
      }
    };
    const response = await api.put(ENDPOINTS.SPRINT_SETTINGS, payload);
    return response.data;
  } catch (error) {
    return rejectWithValue(
      error?.response?.data?.message || 
      error?.response?.data?.error || 
      'Failed to update sprint settings'
    );
  }
});

export const fetchScrumDashboard = createAsyncThunk('scrum/fetchDashboard', async (arg, { rejectWithValue }) => {
  try {
    const projectId = typeof arg === 'object' ? arg.projectId : arg;
    const response = await api.get(ENDPOINTS.SCRUM_DASHBOARD(projectId));
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

export const fetchScrumRole = createAsyncThunk('scrum/fetchScrumRole', async (projectId, { rejectWithValue }) => {
  try {
    const response = await api.get(ENDPOINTS.SCRUM_ROLE(projectId));
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

export const fetchMyScrumTickets = createAsyncThunk('scrum/fetchMyTickets', async ({ projectId, page = 1, limit = 15, search = '', sort = '' }, { rejectWithValue }) => {
  try {
    const response = await api.get(ENDPOINTS.SCRUM_MY_TICKETS(projectId, page, limit, search, sort));
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

export const fetchSprintsTickets = createAsyncThunk('scrum/fetchSprintsTickets', async ({ projectId, state = 'ACTIVE', sprintId, page = 1, limit = 15, search = '', sort = '' }, { rejectWithValue }) => {
  try {
    const response = await api.get(ENDPOINTS.SCRUM_SPRINTS_TICKETS(projectId, state, sprintId, page, limit, search, sort));
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data || error.message);
  }
});

export const fetchSprintTimeline = createAsyncThunk('scrum/fetchSprintTimeline', async ({ projectId, type = 'TIMELINE', status = '', page = 1, limit = 10, append = false }, { rejectWithValue }) => {
  try {
    const params = new URLSearchParams();
    if (type) params.append('type', type);
    if (status) params.append('status', status);
    if (page !== undefined && page !== null) params.append('page', page);
    if (limit !== undefined && limit !== null) params.append('limit', limit);

    const query = params.toString();
    const response = await api.get(`${ENDPOINTS.SCRUM_SPRINT_TIMELINE(projectId)}${query ? `?${query}` : ''}`);
    return {
      type,
      append,
      page,
      data: response.data
    };
  } catch (error) {
    return rejectWithValue({
      type,
      error: error.response?.data || error.message
    });
  }
});

export const fetchSprintDetails = createAsyncThunk('scrum/fetchSprintDetails', async (sprintId, { rejectWithValue }) => {
  try {
    const response = await api.get(ENDPOINTS.SCRUM_SPRINT_BY_ID(sprintId));
    return response.data;
  } catch (error) {
    return rejectWithValue(
      error.response?.data?.message ||
      error.message ||
      'Failed to fetch sprint details'
    );
  }
});

export const fetchSprintVersions = createAsyncThunk('scrum/fetchSprintVersions', async (projectId, { rejectWithValue }) => {
  try {
    const response = await api.get(ENDPOINTS.SCRUM_SPRINT_VERSIONS(projectId));
    return response.data;
  } catch (error) {
    return rejectWithValue(
      error.response?.data?.message ||
      error.message ||
      'Failed to fetch sprint versions'
    );
  }
});

export const createSprint = createAsyncThunk('scrum/createSprint', async ({ projectId, data }, { rejectWithValue }) => {
  try {
    // Both dev and HEAD used different endpoints names, but they resolve to the same route. Use dev's for consistency with SprintModal
    const response = await api.post(ENDPOINTS.SCRUM_CREATE_SPRINT ? ENDPOINTS.SCRUM_CREATE_SPRINT(projectId) : ENDPOINTS.SCRUM_SPRINTS(projectId), data);
    return response.data;
  } catch (error) {
    return rejectWithValue(
      error?.response?.data?.message || 
      error?.response?.data?.error || 
      'Failed to create sprint'
    );
  }
});

export const updateSprint = createAsyncThunk('scrum/updateSprint', async ({ sprintId, data }, { rejectWithValue }) => {
  try {
    const response = await api.put(ENDPOINTS.SCRUM_UPDATE_SPRINT(sprintId), data);
    return response.data;
  } catch (error) {
    return rejectWithValue(
      error?.response?.data?.message || 
      error?.response?.data?.error || 
      'Failed to update sprint'
    );
  }
});

export const updateSprintStatus = createAsyncThunk('scrum/updateSprintStatus', async ({ sprintId, data }, { rejectWithValue }) => {
  try {
    const response = await api.put(ENDPOINTS.SCRUM_SPRINT_UPDATE(sprintId), data);
    toast.success(response?.data?.message || TOAST_MESSAGES.SPRINT_STATUS_UPDATED);
    return response.data;
  } catch (error) {
    toast.error(TOAST_MESSAGES.SPRINT_UPDATE_FAILED);
    return rejectWithValue(error.response?.data || error.message);
  }
});

const scrumSlice = createSlice({
  name: 'scrum',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearScrumData: (state) => {
      state.dashboard = null;
      state.myTickets = null;
      state.sprintsTickets = [];
      state.sprintTimeline = null;
      state.sprintTimelineLoading = false;
      state.sprintSummary = null;
      state.sprintSummaryLoading = false;
      state.scrumRole = null;
      state.sprintManagement = null;
      state.sprintManagementLoading = false;
      state.sprintManagementError = null;
      state.sprintManagementRequestId = null;
    }
  },
  extraReducers: (builder) => {
    // Dashboard
    builder.addCase(fetchScrumDashboard.pending, (state, action) => {
      if (!(typeof action.meta.arg === 'object' && action.meta.arg.background)) {
        state.dashboardLoading = true;
      }
    });
    builder.addCase(fetchScrumDashboard.fulfilled, (state, action) => {
      state.dashboardLoading = false;
      state.dashboard = action.payload?.data || action.payload;
    });
    builder.addCase(fetchScrumDashboard.rejected, (state, action) => {
      state.dashboardLoading = false;
      state.error = action.payload || action.error.message;
    });

    // Scrum Role
    builder.addCase(fetchScrumRole.pending, (state) => {
      state.scrumRoleLoading = true;
    });
    builder.addCase(fetchScrumRole.fulfilled, (state, action) => {
      state.scrumRoleLoading = false;
      state.scrumRole = action.payload?.data || action.payload;
    });
    builder.addCase(fetchScrumRole.rejected, (state, action) => {
      state.scrumRoleLoading = false;
      state.error = action.payload || action.error.message;
    });

    // My Tickets
    builder.addCase(fetchMyScrumTickets.pending, (state, action) => {
      if (!action.meta.arg?.background) {
        state.myTicketsLoading = true;
      }
    });
    builder.addCase(fetchMyScrumTickets.fulfilled, (state, action) => {
      state.myTicketsLoading = false;
      state.myTickets = action.payload?.data || action.payload;
    });
    builder.addCase(fetchMyScrumTickets.rejected, (state, action) => {
      state.myTicketsLoading = false;
      state.error = action.payload || action.error.message;
    });

    // Sprints Tickets
    builder.addCase(fetchSprintsTickets.pending, (state, action) => {
      if (!action.meta.arg?.background) {
        state.sprintsTicketsLoading = true;
      }
    });
    builder.addCase(fetchSprintsTickets.fulfilled, (state, action) => {
      state.sprintsTicketsLoading = false;
      state.sprintsTickets = action.payload?.data || action.payload;
    });
    builder.addCase(fetchSprintsTickets.rejected, (state, action) => {
      state.sprintsTicketsLoading = false;
      state.error = action.payload || action.error.message;
    });

    // Sprint Timeline / Summary
    builder.addCase(fetchSprintTimeline.pending, (state, action) => {
      if (action.meta.arg?.type === 'SUMMARY') {
        state.sprintSummaryLoading = true;
      } else {
        state.sprintTimelineLoading = true;
      }
    });
    builder.addCase(fetchSprintTimeline.fulfilled, (state, action) => {
      const responseData = action.payload?.data?.data || action.payload?.data || null;
      const isSummary = action.payload?.type === 'SUMMARY';
      const shouldAppend = Boolean(action.payload?.append) && !isSummary;

      if (isSummary) {
        state.sprintSummaryLoading = false;
        state.sprintSummary = responseData;
      } else {
        state.sprintTimelineLoading = false;
        if (shouldAppend && state.sprintTimeline && Array.isArray(state.sprintTimeline.data) && Array.isArray(responseData?.data)) {
          state.sprintTimeline = {
            ...responseData,
            data: [...state.sprintTimeline.data, ...responseData.data]
          };
        } else {
          state.sprintTimeline = responseData;
        }
      }
    });
    builder.addCase(fetchSprintTimeline.rejected, (state, action) => {
      const requestType = action.payload?.type || action.meta.arg?.type;
      if (requestType === 'SUMMARY') {
        state.sprintSummaryLoading = false;
      } else {
        state.sprintTimelineLoading = false;
      }

      state.error = action.payload?.error || action.error.message;
    });

    // Sprint Details
    builder.addCase(fetchSprintDetails.pending, (state) => {
      state.sprintDetailsLoading = true;
    });
    builder.addCase(fetchSprintDetails.fulfilled, (state, action) => {
      state.sprintDetailsLoading = false;
      state.sprintDetails = action.payload?.data || action.payload;
    });
    builder.addCase(fetchSprintDetails.rejected, (state, action) => {
      state.sprintDetailsLoading = false;
      state.error = action.payload;
    });

    // Sprint Versions
    builder.addCase(fetchSprintVersions.pending, (state) => {
      state.sprintVersionsLoading = true;
    });
    builder.addCase(fetchSprintVersions.fulfilled, (state, action) => {
      state.sprintVersionsLoading = false;
      state.sprintVersions = action.payload?.data || [];
    });
    builder.addCase(fetchSprintVersions.rejected, (state, action) => {
      state.sprintVersionsLoading = false;
      state.error = action.payload;
    });

    // Action Loadings (Update Sprint Status)
    builder.addCase(updateSprintStatus.pending, (state) => {
      state.actionLoading = true;
    });
    builder.addCase(updateSprintStatus.fulfilled, (state) => {
      state.actionLoading = false;
    });
    builder.addCase(updateSprintStatus.rejected, (state) => {
      state.actionLoading = false;
    });
    
    // Dev branch thunks
    builder.addCase(createSprint.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createSprint.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(createSprint.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || action.error.message;
    });
    builder.addCase(updateSprint.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateSprint.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(updateSprint.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || action.error.message;
    });

    // Sprint Settings
    builder.addCase(fetchSprintSettings.pending, (state) => {
      state.sprintSettingsLoading = true;
    });
    builder.addCase(fetchSprintSettings.fulfilled, (state, action) => {
      state.sprintSettingsLoading = false;
      state.sprintSettings = action.payload?.data || action.payload;
    });
    builder.addCase(fetchSprintSettings.rejected, (state, action) => {
      state.sprintSettingsLoading = false;
      state.error = action.payload || action.error.message;
    });

    builder.addCase(updateSprintSettings.pending, (state) => {
      state.actionLoading = true;
    });
    builder.addCase(updateSprintSettings.fulfilled, (state, action) => {
      state.actionLoading = false;
      if (action.payload?.data || action.payload) {
        state.sprintSettings = action.payload?.data || action.payload;
      }
    });
    builder.addCase(updateSprintSettings.rejected, (state, action) => {
      state.actionLoading = false;
      state.error = action.payload || action.error.message;
    });

    builder.addCase(fetchSprintManagement.pending, (state, action) => {
      state.sprintManagementLoading = true;
      state.sprintManagementError = null;
      state.sprintManagementRequestId = action.meta.requestId;
    });
    builder.addCase(fetchSprintManagement.fulfilled, (state, action) => {
      if (state.sprintManagementRequestId !== action.meta.requestId) return;
      state.sprintManagementLoading = false;
      // Each request contains the latest identity data for every sprint card,
      // while the selected card also carries its detailed metrics. Preserve
      // previously loaded metrics for an unchanged sprint when another card is
      // selected; incoming fields always win so all values supplied by the
      // latest response are reflected immediately.
      const previousSnapshot = state.sprintManagement?.snapshot || {};
      const nextSnapshot = action.payload?.snapshot || {};
      const snapshot = Object.keys(nextSnapshot).reduce((merged, status) => {
        const previousSprint = previousSnapshot[status];
        const nextSprint = nextSnapshot[status];
        const isSameSprint = previousSprint?.id && nextSprint?.id &&
          String(previousSprint.id) === String(nextSprint.id);

        merged[status] = isSameSprint
          ? { ...previousSprint, ...nextSprint }
          : nextSprint;
        return merged;
      }, {});

      state.sprintManagement = {
        ...action.payload,
        snapshot
      };
      state.sprintManagementRequestId = null;
    });
    builder.addCase(fetchSprintManagement.rejected, (state, action) => {
      if (state.sprintManagementRequestId !== action.meta.requestId) return;
      state.sprintManagementLoading = false;
      state.sprintManagementError = action.payload || action.error.message;
      state.sprintManagementRequestId = null;
    });
  }
});

export const { clearError, clearScrumData } = scrumSlice.actions;
export default scrumSlice.reducer;
